/**
 * Application header (Angular 18)
 *
 * The full application-mode header: the shared chrome (`app-header-shell`), the
 * non-cart user nav (profile link, Logout, or Sign In) derived from the
 * AuthenticationService signals, and an `<ng-content>` slot into which the
 * AngularJS `<header-cart>` is projected for cart tenants.
 *
 * Migrated from the AngularJS `header` directive's application template
 * (apps/b2b-ui/common/directives/header/application.tpl.html). The cart popover
 * itself is NOT migrated — it stays AngularJS (`<header-cart>`) and is projected
 * in, compiled by AngularJS. The cart self-gates on `useCart`, and the non-cart
 * nav is gated on `!useCart`, so exactly one is visible.
 *
 * Downgraded for AngularJS as `<ng-header-app>`. Uses `@Input()`-free derivation
 * (services) like the minimal/print headers; navigation goes through STATE_PORT.
 */
import { Component, inject } from '@angular/core';
import { NgIf } from '@angular/common';

import { AuthenticationService } from '../../../core/auth/authentication.service';
import { FeatureToggleService } from '../../../core/feature-toggle/feature-toggle.service';
import { ThemesHelperService } from '../../../core/themes-helper/themes-helper.service';
import { STATE_PORT } from '../../../core/utils';
import { HeaderShellComponent } from '../header-shell/header-shell.component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [NgIf, HeaderShellComponent],
  template: require('./header-app.component.html'),
})
export class HeaderAppComponent {
  private readonly auth = inject(AuthenticationService);
  private readonly featureToggle = inject(FeatureToggleService);
  private readonly themesHelper = inject(ThemesHelperService);
  private readonly state = inject(STATE_PORT);

  /** Reactive login state (signal off AuthenticationService) — replaces the old
   *  `AppController::login/logout::success` listeners. */
  readonly isLoggedIn = this.auth.isLoggedIn;

  /** Whether the cart UI is enabled (drives cart slot vs. non-cart nav). */
  readonly useCart = this.featureToggle.check('TENANT_CART');

  /** Company display name (shell company-name line shows it for non-cart sessions). */
  get companyName(): string {
    return this.themesHelper.getCompany('name') || `Tenant ID: ${this.auth.user()?.tenantId ?? ''}`;
  }

  /** Profile link label (handles CSR impersonation labelling). */
  get userFullName(): string {
    return this.auth.getUserFullName();
  }

  goToProfile(): void {
    this.state.go('application.account.profile');
  }

  logout(): void {
    this.state.go('logout.default');
  }

  goToLogin(): void {
    this.state.go('login.default');
  }
}
